import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const baseUrl = process.env.REACT_APP_BASE_URL || "";

const buildUrl = (path) => {
  const trimmedBase = baseUrl.replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${trimmedBase}${normalizedPath}`;
};

const normalizeListResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const data = payload.data ?? payload.result ?? payload;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.blogs)) return data.blogs;
  if (Array.isArray(payload.blogs)) return payload.blogs;
  // Keep announcement as fallback for backward compatibility
  if (Array.isArray(data?.announcements)) return data.announcements;
  if (Array.isArray(payload.announcements)) return payload.announcements;

  const single =
    data?.blog ??
    payload.blog ??
    // Keep announcement as fallback for backward compatibility
    data?.announcement ??
    payload.announcement ??
    null;

  return single ? [single] : [];
};

const normalizeSingleResponse = (payload) => {
  if (!payload) return null;
  const data = payload.data ?? payload.result ?? payload;
  return (
    data?.blog ??
    payload.blog ??
    // Keep announcement as fallback for backward compatibility
    data?.announcement ??
    payload.announcement ??
    data
  );
};

const extractErrorMessage = async (response) => {
  try {
    const data = await response.clone().json();
    return (
      data?.message ||
      data?.error ||
      data?.errors?.[0] ||
      `Request failed with status ${response.status}`
    );
  } catch (err) {
    return `Request failed with status ${response.status}`;
  }
};

const uploadBlogImage = async (file, token) => {
  if (!file) return undefined;

  const formData = new FormData();
  formData.append("inspectReport", file);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const response = await fetch(`${baseUrl}/api/upload`, {
    method: "POST",
    // Do not set Content-Type so browser adds proper multipart boundary
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(await extractErrorMessage(response));
  }

  const data = await response.json();

  // Extract URL defensively (API response may vary)
  return (
    data?.imageUrl ||
    data?.url ||
    data?.fileUrl ||
    data?.files?.[0]?.url ||
    data?.uploaded?.[0]?.url
  );
};

export const fetchBlogs = createAsyncThunk(
  "blog/fetchBlogs",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(buildUrl("/api/blog"), {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(await extractErrorMessage(response));
      }

      const data = await response.json();
      return normalizeListResponse(data);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch blogs");
    }
  }
);

export const createBlog = createAsyncThunk(
  "blog/createBlog",
  async ({ title, description, imageFile }, { rejectWithValue }) => {
    try {
      if (!title?.trim()) {
        throw new Error("Title is required");
      }
      if (!description?.trim()) {
        throw new Error("Description is required");
      }

      const token = localStorage.getItem("token");
      let imageUrl;

      if (imageFile) {
        imageUrl = await uploadBlogImage(imageFile, token);
      }

      const response = await fetch(buildUrl("/api/blog"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          title,
          description,
          ...(imageUrl ? { img: imageUrl } : {}),
        }),
      });

      if (!response.ok) {
        throw new Error(await extractErrorMessage(response));
      }

      const data = await response.json();
      return normalizeSingleResponse(data);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create blog");
    }
  }
);

export const updateBlog = createAsyncThunk(
  "blog/updateBlog",
  async ({ blogId, title, description, imageFile }, { rejectWithValue }) => {
    try {
      if (!blogId) {
        throw new Error("Blog id is required");
      }
      if (!title?.trim()) {
        throw new Error("Title is required");
      }
      if (!description?.trim()) {
        throw new Error("Description is required");
      }

      const token = localStorage.getItem("token");
      let imageUrl;

      if (imageFile) {
        imageUrl = await uploadBlogImage(imageFile, token);
      }

      const body = {
        title,
        description,
      };

      if (imageUrl) {
        body.img = imageUrl;
      }

      const response = await fetch(buildUrl(`/api/blog/${blogId}`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(await extractErrorMessage(response));
      }

      const data = await response.json();
      return normalizeSingleResponse(data);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update blog");
    }
  }
);

export const deleteBlog = createAsyncThunk(
  "blog/deleteBlog",
  async (blogId, { rejectWithValue }) => {
    try {
      if (!blogId) {
        throw new Error("Blog id is required");
      }

      const token = localStorage.getItem("token");

      const response = await fetch(buildUrl(`/api/blog/${blogId}`), {
        method: "DELETE",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(await extractErrorMessage(response));
      }

      return blogId;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete blog");
    }
  }
);

const initialState = {
  items: [],
  status: "idle",
  error: null,
  createStatus: "idle",
  createError: null,
  updateStatus: "idle",
  updateError: null,
  deleteStatus: "idle",
  deleteError: null,
};

const blogSlice = createSlice({
  name: "blog",
  initialState,
  reducers: {
    resetBlogState: () => initialState,
    clearBlogErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlogs.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload ?? [];
      })
      .addCase(fetchBlogs.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch blogs";
      })
      .addCase(createBlog.pending, (state) => {
        state.createStatus = "loading";
        state.createError = null;
      })
      .addCase(createBlog.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        const blog = action.payload;
        if (blog) {
          const exists = state.items.some(
            (item) => item?._id && item._id === blog._id
          );
          if (!exists) {
            state.items = [blog, ...state.items];
          } else {
            state.items = state.items.map((item) =>
              item._id === blog._id ? { ...item, ...blog } : item
            );
          }
        }
      })
      .addCase(createBlog.rejected, (state, action) => {
        state.createStatus = "failed";
        state.createError =
          action.payload ||
          action.error?.message ||
          "Failed to create blog";
      })
      .addCase(updateBlog.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(updateBlog.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        const blog = action.payload;
        if (blog) {
          state.items = state.items.map((item) =>
            item?._id === blog._id ? { ...item, ...blog } : item
          );
        }
      })
      .addCase(updateBlog.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError =
          action.payload ||
          action.error?.message ||
          "Failed to update blog";
      })
      .addCase(deleteBlog.pending, (state) => {
        state.deleteStatus = "loading";
        state.deleteError = null;
      })
      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        const blogId = action.payload;
        state.items = state.items.filter((blog) => blog?._id !== blogId);
      })
      .addCase(deleteBlog.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.deleteError =
          action.payload ||
          action.error?.message ||
          "Failed to delete blog";
      });
  },
});

export const { resetBlogState, clearBlogErrors } = blogSlice.actions;

export default blogSlice.reducer;

